import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { api } from '../../api/client';
import { fine, money, date as fmtDate, weight } from '../../utils/format';
import { ErrorNotice, Empty, Loading, PageHeader } from '../../components/ui';
import { DateInput } from '../../components/ui/Form';

interface ReportMeta {
  slug: string;
  title: string;
  group: string;
  description: string;
  filters: string[];
  columns: { key: string; label: string; type: string }[];
}

interface ReportResult {
  title: string;
  columns: { key: string; label: string; type: string }[];
  rows: Record<string, string | null>[];
  totals: Record<string, string> | null;
  rowCount: number;
  truncated: boolean;
}

const cell = (value: string | null, type: string): string => {
  if (value === null || value === undefined) return '—';
  switch (type) {
    case 'money':
      return money(value);
    case 'fine':
      return fine(value);
    case 'weight':
      return weight(value);
    case 'date':
      return fmtDate(value);
    case 'percent':
      return `${value}%`;
    default:
      return value;
  }
};

const numeric = (type: string): boolean => ['money', 'fine', 'weight', 'number', 'percent'].includes(type);

/** The footer row, split out so the null check narrows for the whole table. */
function ReportTotals({
  columns,
  totals,
}: {
  columns: { key: string; label: string; type: string }[];
  totals: Record<string, string> | null;
}) {
  if (!totals) return null;
  return (
    <tfoot className="bg-gold-50/60">
      <tr>
        {columns.map((c, i) => (
          <td key={c.key} className={`td font-semibold ${numeric(c.type) ? 'num text-right' : ''}`}>
            {i === 0 ? 'Total' : cell(totals[c.key] ?? null, c.type)}
          </td>
        ))}
      </tr>
    </tfoot>
  );
}

/** What each group of reports is for, in one line. */
const GROUP_BLURB: Record<string, string> = {
  Sales: 'What was sold, to whom, at what profit — and the quotes that led to it.',
  Purchase: 'What came in from suppliers, and what was bought back from customers.',
  Stock: 'What is on the shelves, where it is, what it weighs and what is not moving.',
  Accounting: 'The day book, cash book, outstandings, GST and expenses.',
  Melting: 'Melts, bars and loss by melter, and the metal still out at the refinery.',
  Control: 'Adjustments and the exceptions a manager approved.',
};

/** The Indian financial year containing today: 1 April to 31 March. */
function financialYear(): { from: string; to: string } {
  const now = new Date();
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}

export function ReportsPage() {
  const { header: printHeader } = usePrintHeader();
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState('');

  /** A download that fails should say so, not simply not happen. */
  const save = (format: 'xlsx' | 'csv', slug: string, q: string): void => {
    setDownloadError(null);
    setDownloading(format);
    void api
      .download(
        `/v1/reports/${slug}/export?${q}&format=${format}`,
        `${slug}-${new Date().toISOString().slice(0, 10)}.${format}`,
      )
      .catch(setDownloadError)
      .finally(() => setDownloading(''));
  };
  const navigate = useNavigate();
  // The menu opens a group directly: /reports?group=Sales shows the sales
  // registers and opens the first of them, rather than a wall of thirty.
  const [params, setParams] = useSearchParams();
  const group = params.get('group');
  // ?report=<slug> opens one report directly, from a link on another screen.
  const [slug, setSlug] = useState<string | null>(params.get('report'));
  // Registers open on the current financial year — the period a shop files
  // and reports on — rather than every transaction since the books began.
  const [from, setFrom] = useState(financialYear().from);
  const [to, setTo] = useState(financialYear().to);

  const catalogue = useQuery({
    queryKey: ['reports'],
    queryFn: () => api.get<{ data: ReportMeta[] }>('/reports'),
  });

  const selected = catalogue.data?.data.find((r) => r.slug === slug) ?? null;
  const query = new URLSearchParams();
  if (selected?.filters.includes('from') && from) query.set('from', from);
  if (selected?.filters.includes('to') && to) query.set('to', to);

  const result = useQuery({
    queryKey: ['report', slug, query.toString()],
    queryFn: () => api.get<ReportResult>(`/reports/${slug}/run?${query.toString()}`),
    enabled: slug !== null,
  });

  const all = catalogue.data?.data ?? [];
  const groups = [...new Set(all.map((r) => r.group))];
  const shown = group ? all.filter((r) => r.group === group) : all;

  // Opening a group selects its first report, so the panel is never blank.
  useEffect(() => {
    if (shown.length === 0) return;
    if (!slug || !shown.some((r) => r.slug === slug)) setSlug(shown[0]!.slug);
  }, [group, catalogue.data]);

  return (
    <>
      <PageHeader
        title={group ? `${group} reports` : 'Reports'}
        subtitle={
          group
            ? (GROUP_BLURB[group] ?? 'Read from the ledger.')
            : 'Registers, stock, GST and control reports — everything read from the ledger.'
        }
        actions={
          <button className="btn-primary" onClick={() => navigate('/statements')}>
            Financial statements
          </button>
        }
      />

      {/* The groups, as tabs: one click between sales and stock. */}
      <div className="no-print mb-4 flex flex-wrap items-center gap-1 border-b border-silver-100">
        <button
          onClick={() => setParams({})}
          className={`border-b-2 px-3 py-1.5 text-sm ${
            !group
              ? 'border-gold-600 font-medium text-gold-700'
              : 'border-transparent text-ink-soft hover:text-gold-700'
          }`}
        >
          All
        </button>
        {groups.map((g) => (
          <button
            key={g}
            onClick={() => setParams({ group: g })}
            className={`border-b-2 px-3 py-1.5 text-sm ${
              group === g
                ? 'border-gold-600 font-medium text-gold-700'
                : 'border-transparent text-ink-soft hover:text-gold-700'
            }`}
          >
            {g}
          </button>
        ))}
      </div>
      <ErrorNotice error={catalogue.error ?? result.error ?? downloadError} />

      {/* The report is chosen from a row of chips, so the table has the whole
          width of the page — a register with a dozen columns needs it. */}
      <div className="no-print mb-4 flex flex-wrap gap-1.5">
        {shown.map((r) => (
          <button
            key={r.slug}
            onClick={() => setSlug(r.slug)}
            title={r.description}
            className={`rounded-full border px-3 py-1.5 text-sm ${
              slug === r.slug
                ? 'border-velvet bg-velvet font-medium text-white'
                : 'border-silver-100 bg-surface text-ink-soft hover:border-gold-400 hover:text-gold-700'
            }`}
          >
            {r.title}
          </button>
        ))}
      </div>

      <div>
        <section>
          {!selected ? (
            <Empty message="Choose a report above." />
          ) : (
            <>
              <div className="no-print mb-4 flex flex-wrap items-end gap-3">
                <div>
                  <p className="text-sm font-medium">{selected.title}</p>
                  <p className="text-xs text-ink-soft">{selected.description}</p>
                </div>

                {selected.filters.includes('from') ? (
                  <div>
                    <label className="label" htmlFor="from">
                      From
                    </label>
                    <DateInput value={from} onChange={(v) => setFrom(v)} />
                  </div>
                ) : null}
                {selected.filters.includes('to') ? (
                  <div>
                    <label className="label" htmlFor="to">
                      To
                    </label>
                    <DateInput value={to} onChange={(v) => setTo(v)} />
                  </div>
                ) : null}

                <div className="ml-auto flex flex-wrap gap-2">
                  <button
                    className="btn-primary"
                    onClick={() => save('xlsx', selected.slug, query.toString())}
                    disabled={downloading !== ''}
                  >
                    {downloading === 'xlsx' ? 'Preparing…' : 'Excel'}
                  </button>
                  <button
                    className="btn-ghost"
                    onClick={() => {
                      // The print sheet carries the shop's name and the period;
                      // the browser's "Save as PDF" destination turns it into a
                      // PDF with real text rather than a picture of a table.
                      const was = document.title;
                      document.title = `${selected.title} ${from || ''}${to ? ` to ${to}` : ''}`.trim();
                      window.print();
                      window.setTimeout(() => {
                        document.title = was;
                      }, 1000);
                    }}
                  >
                    PDF / print
                  </button>
                  <button
                    className="btn-quiet"
                    onClick={() => save('csv', selected.slug, query.toString())}
                    disabled={downloading !== ''}
                  >
                    CSV
                  </button>
                </div>
              </div>

              {result.isLoading ? <Loading what="Running" /> : null}

              {/* A register with many columns goes on its side, so every
                  column reaches the paper. */}
              {result.data && result.data.columns.length > 7 ? (
                <style>{'@media print { @page { size: A4 landscape; margin: 10mm; } }'}</style>
              ) : null}

              {/* Only on paper: who this belongs to and what it covers. */}
              {result.data && result.data.rows.length > 0 ? (
                <div className="hidden print:mb-4 print:block print:border-b-2 print:border-gold-600 print:pb-2 print:text-center">
                  <p className="text-base font-bold">{printHeader.companyName}</p>
                  {printHeader.branchAddress ? <p className="text-2xs">{printHeader.branchAddress}</p> : null}
                  <p className="mt-1 text-sm font-semibold uppercase tracking-wide">{selected.title}</p>
                  <p className="num text-2xs">
                    {from || to ? `${from || 'the beginning'} to ${to || 'today'}` : 'As at today'}
                    {' · '}
                    {result.data.rowCount} {result.data.rowCount === 1 ? 'row' : 'rows'}
                  </p>
                </div>
              ) : null}

              {result.data ? (
                result.data.rows.length === 0 ? (
                  <Empty message="Nothing to show for those dates." />
                ) : (
                  <div
                    className="print-sheet overflow-hidden rounded-xl border border-silver-100 bg-surface shadow-card"
                    // A sheet of paper suits a short register; a piece-by-piece one
                    // with fifteen columns needs the whole screen, or its figures
                    // are pushed off to the right where nobody scrolls to them.
                    style={result.data.columns.length > 8 ? { maxWidth: 'none', padding: 0 } : undefined}
                  >
                    {/* On screen, a heading strip so the table is never a bare
                        grid floating on the page. */}
                    <div className="no-print flex flex-wrap items-baseline justify-between gap-2 border-b border-silver-100 bg-gold-50/60 px-4 py-2.5">
                      <h2 className="text-sm font-semibold">{selected.title}</h2>
                      <span className="text-2xs text-ink-soft">
                        {from || to ? `${from || 'the beginning'} to ${to || 'today'}` : 'As at today'}
                        {' · '}
                        <span className="num">{result.data.rowCount}</span>{' '}
                        {result.data.rowCount === 1 ? 'row' : 'rows'}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full print:text-[11px]">
                        <thead className="bg-gold-50">
                          <tr>
                            {result.data.columns.map((c) => (
                              <th key={c.key} className={`th ${numeric(c.type) ? 'text-right' : ''}`}>
                                {c.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.rows.map((row, i) => (
                            <tr key={i} className={i % 2 ? 'bg-silver-50/40' : ''}>
                              {result.data.columns.map((c) => (
                                <td
                                  key={c.key}
                                  className={`td ${numeric(c.type) ? 'num text-right' : ''} ${
                                    numeric(c.type) || c.type === 'date' ? 'whitespace-nowrap' : ''
                                  }`}
                                >
                                  {cell(row[c.key] ?? null, c.type)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                        <ReportTotals columns={result.data.columns} totals={result.data.totals} />
                      </table>
                    </div>
                  </div>
                )
              ) : null}

              {result.data?.truncated ? (
                <p className="mt-2 text-xs text-ink-soft">
                  Showing the first {result.data.rowCount} rows. Narrow the dates, or export the whole thing.
                </p>
              ) : null}
            </>
          )}
        </section>
      </div>
    </>
  );
}
